import { WELCOME_HELP_URL } from "@/lib";
import { Trans, useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import styles from "@/styles/BuildingSettings.module.scss";
import { createFileRoute } from "@tanstack/react-router";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import { buildingAdminsOptions, getImageOptions, useBuilding } from "@/api";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import { useWelcomeAccessAccess } from "@/hooks/useWelcomeAccessAccess";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import GeneralBuildingSettings from "@/components/building_admin/building_admin_settings/general";
import BuildingSettingCard from "@/components/building_admin/building_admin_settings/BuildingSettingsCard";
import { useSuspenseQueries } from "@tanstack/react-query";
import { useRouterStore } from "@/store/zustandStore";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { useBuildingWelcomeVisitAccess } from "@/hooks/useBuildingWelcomeVisitAccess";

export const Route = createFileRoute("/_portal/building/settings/")({
  component: BuildingSettings,
  loader: () => {
    const { queryClient, building } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(buildingAdminsOptions());
    queryClient.ensureQueryData(getImageOptions(building?.ownerLogoImgUrl));
    queryClient.ensureQueryData(getImageOptions(building?.teamPhotoImgUrl));
    queryClient.ensureQueryData(
      getImageOptions(building?.buildingBannerImgUrl),
    );
  },
});

function BuildingSettings() {
  const { t } = useTranslation();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess(true);
  const { buildingIntegratedWithAccessControlSystem } =
    useWelcomeAccessAccess(true);
  const { hasCommunication } = useWelcomeCommunicationAccess(true);
  const { hasEitherFoodSystem } = useWelcomeFoodAccess(true);
  const { snackEnabledAndHasSupplier } = useWelcomeSnackAccess(true);
  const hasWelcomeVisit = useBuildingWelcomeVisitAccess(true);

  const { allowBuildingAdminOrReceptionistAccess, isBuildingAdmin } =
    useBuildingAdminOrReceptionistAccess();
  const tabsReceptionistHasAccessTo = ["visit", "booking"];

  const { data: building } = useBuilding();

  const [
    { data: ownerLogoImage },
    { data: teamPhotoImage },
    { data: buildingBannerImage },
    { data: allBuildingAdmins },
  ] = useSuspenseQueries({
    queries: [
      getImageOptions(building?.ownerLogoImgUrl),
      getImageOptions(building?.teamPhotoImgUrl),
      getImageOptions(building?.buildingBannerImgUrl),
      buildingAdminsOptions(),
    ],
  });

  const tabs = [
    {
      icon: "group",
      value: "visit",
      label: "Welcome Visit",
      enabled: hasWelcomeVisit,
      to: "/building/settings/visit",
      descriptionKey: "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.VISIT",
    },
    {
      icon: "date_range",
      value: "booking",
      label: "Welcome Booking",
      enabled: allowWelcomeBookingAccess,
      to: "/building/settings/booking",
      descriptionKey: "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.BOOKING",
    },
    {
      icon: "badge",
      value: "access",
      label: "Welcome Access",
      enabled: buildingIntegratedWithAccessControlSystem,
      to: "/building/settings/access",
      descriptionKey: "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.ACCESS",
    },
    {
      icon: "campaign",
      value: "communication",
      label: "Welcome Communication",
      enabled: hasCommunication,
      to: "/building/settings/communication",
      descriptionKey:
        "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.COMMUNICATION",
    },
    {
      icon: "burger",
      value: "food",
      label: "Welcome Food",
      enabled: hasEitherFoodSystem || snackEnabledAndHasSupplier,
      to: "/building/settings/food",
      descriptionKey: "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.FOOD",
    },
    // {
    //   icon: "clinical_notes",
    //   value: "compliance",
    //   label: "Welcome Compliance",
    //   enabled: false,
    //   comingSoon: true,
    //   to: "/building/settings/compliance",
    //   descriptionKey: "PAGES.BUILDING_SETTINGS.MODULE_DESCRIPTIONS.COMPLIANCE",
    // },
  ];

  const visibleTabs = isBuildingAdmin
    ? tabs
    : tabs.filter((t) => tabsReceptionistHasAccessTo.includes(t.value));

  if (!allowBuildingAdminOrReceptionistAccess) {
    return null;
  }

  return (
    <>
      <div className="pageHeader">
        <h1>{t("PAGES.BUILDING_SETTINGS.HEADER")}</h1>
        <p>
          <Trans i18nKey="PAGES.BUILDING_SETTINGS.SUBHEADER">
            Text
            <a href={WELCOME_HELP_URL(i18n.language)} target="_blank"></a>.
          </Trans>
        </p>
      </div>
      <div className={styles.cardContainer}>
        {visibleTabs.map((tab) => (
          <BuildingSettingCard isLink key={tab.value} tab={tab} />
        ))}
      </div>
      {isBuildingAdmin && (
        <div className={styles.generalSettingsContainer}>
          <h2>{t("GENERAL_SETTINGS")}</h2>
          <GeneralBuildingSettings
            ownerLogoImage={ownerLogoImage}
            teamPhotoImage={teamPhotoImage}
            buildingBannerImage={buildingBannerImage}
            allBuildingAdmins={allBuildingAdmins}
          />
        </div>
      )}
    </>
  );
}
